/** @format */

import { i18nContext } from '@/context/i18nContext';
import { Entry } from 'contentful';
import { FunctionComponent as FC, useCallback, useContext, useRef, useState } from 'react';
import styled, { ThemeContext } from 'styled-components';
import 'swiper/css';
import 'swiper/css/grid';
import { Grid, Keyboard } from 'swiper/modules';
import { SwiperOptions } from 'swiper/types';
import ChevronLeft from '../../assets/icons/arrow_left.svg';
import ChevronRight from '../../assets/icons/arrow_right.svg';
import { breakpointFrom } from '../../theme/mixins';
import { ModalLeadership } from '../../types/page';
import { Modal } from '../Modal';
import ModalContent from '../Modal/ModalContent';
import { Icon } from '../ui/Icon';
import { Container, ContainerWrapper } from '../ui/Layout';
import { H2 } from '../ui/Typography';
import { TeamCard } from './TeamCard';

import { Swiper, SwiperSlide } from 'swiper/react';

export const TeamSection: FC<Props> = ({ title, cards }) => {
  const theme = useContext(ThemeContext);
  const [openModalIndex, setOpenModalIndex] = useState(-1);

  const swiperRef = useRef<any>(null);
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const [isLastSlide, setIsLastSlide] = useState(false);

  const handlePrevious = useCallback(() => {
    swiperRef.current?.swiper.slidePrev();
  }, [swiperRef]);

  const handleNext = useCallback(() => {
    swiperRef.current?.swiper.slideNext();
  }, [swiperRef]);

  return (
    <i18nContext.Consumer>
      {t => (
        <>
          <ContainerWrapper as="section" $color={theme?.color.white}>
            <Wrapper>
              <H2 $color={theme?.color.teal.dark}>{title}</H2>
              <CardsWrapper
                keyboard={{
                  enabled: true,
                }}
                id="TeamSectionSwiper"
                breakpoints={BREAKPOINTS}
                updateOnWindowResize={true}
                modules={[Grid, Keyboard]}
                ref={swiperRef}
                allowSlidePrev={!isFirstSlide}
                allowSlideNext={!isLastSlide}
                onReachBeginning={e => {
                  setIsFirstSlide(e.isBeginning);
                }}
                onSlideChange={e => {
                  setIsFirstSlide(e.isBeginning);
                  setIsLastSlide(e.isEnd);
                }}
                onReachEnd={e => {
                  setIsLastSlide(e.isEnd);
                }}
              >
                {cards.map(({ fields }, i) => (
                  <SwiperSlide key={fields.title}>
                    <TeamCard card={fields} openModal={() => setOpenModalIndex(i)} />
                  </SwiperSlide>
                ))}
                <div className="controls">
                  <button onClick={handlePrevious} title={t['aria-label']['previousSlide']} disabled={isFirstSlide}>
                    <Arrow icon={ChevronLeft} width={44} height={44} />
                  </button>
                  <button onClick={handleNext} title={t['aria-label']['nextSlide']} disabled={isLastSlide}>
                    <Arrow icon={ChevronRight} width={44} height={44} />
                  </button>
                </div>
              </CardsWrapper>
              <GridContainer>
                {cards.map(({ fields }, i) => (
                  <TeamCard key={`${fields.title}-${i + 1}`} card={fields} openModal={() => setOpenModalIndex(i)} />
                ))}
              </GridContainer>
            </Wrapper>
          </ContainerWrapper>

          {cards.map(({ fields }, i) => (
            <Modal key={fields.title} isOpen={openModalIndex === i} onClose={() => setOpenModalIndex(-1)}>
              <ModalContent modal={fields} isMain />
            </Modal>
          ))}
        </>
      )}
    </i18nContext.Consumer>
  );
};

type Props = {
  title: string;
  cards: Entry<ModalLeadership>[];
};

const Wrapper = styled(Container)`
  h2 {
    text-align: center;
    padding-block: clamp(2rem, 1.0714rem + 3.8095vw, 4.5rem) clamp(3.75rem, 3.1rem + 2.6667vw, 5.5rem);
  }
`;

const CardsWrapper = styled(Swiper)`
  .swiper-wrapper {
    margin-inline: auto;
  }
  ${breakpointFrom.lg`
      display: none!important;
  `}

  .controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin-top: 2rem;

    ${breakpointFrom.lg`
      display: none;
    `}
  }

  button {
    display: grid;
    place-content: center;
    border-radius: 50%;
    cursor: pointer;
    border: none;
  }
`;

const GridContainer = styled.div`
  grid-column-gap: clamp(2rem, 0.4367rem + 6.9481vw, 6.69rem);
  grid-row-gap: 5.31rem;
  display: none;
  flex-wrap: wrap;
  justify-content: center;

  ${breakpointFrom.lg`
      display: flex;
    `}
`;

type Breakpoints =
  | {
      [width: number]: SwiperOptions;
      [ratio: string]: SwiperOptions;
    }
  | undefined;
type FillType = 'row' | 'column' | undefined;
const FILL = 'row' as FillType;

const BREAKPOINTS: Breakpoints = {
  0: {
    slidesPerView: 1,
    grid: {
      rows: 1,
      fill: FILL,
    },
  },
  370: {
    slidesPerView: 1.2,
    spaceBetween: 15,
  },
  390: {
    slidesPerView: 1.2,
    spaceBetween: 35,
  },
  410: {
    slidesPerView: 1.2,
    spaceBetween: 15,
  },
  430: {
    slidesPerView: 1.2,
    spaceBetween: 30,
  },
  460: {
    slidesPerView: 1.4,
    spaceBetween: 5,
  },
  530: {
    slidesPerView: 1.4,
    spaceBetween: 5,
  },
  585: {
    slidesPerView: 1.6,
    spaceBetween: 5,
  },
  768: {
    grid: {
      rows: 4,
      fill: FILL,
    },
    spaceBetween: 32,
    slidesPerView: 2,
  },
};

const Arrow = styled(Icon)`
  mask-size: 200%;
  mask-position: center;
`;
